import { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";

export default function Backtest() {
  const [strategies, setStrategies] = useState([]);
  const [symbol, setSymbol] = useState("SPY");
  const [strategy, setStrategy] = useState("sma_crossover");
  const [params, setParams] = useState({ fast: 20, slow: 50 });
  const [period, setPeriod] = useState("2y");
  const [capital, setCapital] = useState(100000);
  const [commission, setCommission] = useState(0.05);
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get("/backtest/strategies").then(({ data }) => setStrategies(data.strategies || []));
  }, []);

  const onStrategy = (id) => {
    setStrategy(id);
    const s = strategies.find((x) => x.id === id);
    if (s) setParams({ ...s.params });
  };

  const run = async () => {
    setBusy(true);
    setR(null);
    try {
      const { data } = await api.post("/backtest/run", {
        symbol: symbol.toUpperCase(), strategy, params,
        period, interval: "1d",
        initial_capital: parseFloat(capital),
        commission_pct: parseFloat(commission),
        slippage_pct: 0,
      });
      setR(data);
      toast.success(`Backtest done · ${data.bars} bars · Sharpe ${data.metrics.sharpe}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object") {
        const s = (detail.suggestions || []).slice(0, 3).map((x) => x.symbol).filter(Boolean).join(", ");
        toast.error(s ? `${detail.message} · Try: ${s}` : detail.message || "Backtest failed", { duration: 6000 });
      } else {
        toast.error(detail || "Backtest failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="backtest-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-6">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ INSTITUTIONAL BACKTESTING ENGINE</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Strategy Lab</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2 max-w-2xl">
          Test strategies against historical price data. Equity curve · Sharpe · Sortino · Calmar · Max DD.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border-default)] mb-4" data-testid="strategy-picker">
        {strategies.map((s) => (
          <button
            key={s.id}
            data-testid={`bt-strategy-${s.id}`}
            onClick={() => onStrategy(s.id)}
            className={`p-3 border-r border-b border-[color:var(--border-default)] text-left transition-colors ${strategy === s.id ? "bg-[color:var(--bg-hover)]" : "hover:bg-[color:var(--bg-elevated)]"}`}
          >
            <div className={`text-sm font-body ${strategy === s.id ? "text-gold" : "text-white"}`}>{s.name}</div>
            <div className="text-[10px] font-body text-[color:var(--text-secondary)] mt-1">{s.description}</div>
          </button>
        ))}
      </div>

      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="bt-inputs">
        <FieldIn label="SYMBOL" value={symbol} onChange={(v) => setSymbol(v.toUpperCase())} testid="bt-symbol" />
        <FieldIn label="PERIOD" value={period} onChange={setPeriod} testid="bt-period" />
        <FieldIn label="CAPITAL" value={capital} onChange={setCapital} testid="bt-capital" type="number" />
        <FieldIn label="COMMISSION %" value={commission} onChange={setCommission} testid="bt-commission" type="number" />
        {Object.entries(params).map(([k, v]) => (
          <FieldIn key={k} label={k.toUpperCase()} value={v} onChange={(val) => setParams({ ...params, [k]: val })} testid={`bt-param-${k}`} type="number" />
        ))}
        <button data-testid="bt-run-btn" onClick={run} disabled={busy} className="bg-white text-black text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-40 transition-colors md:col-span-1">
          {busy ? "RUNNING…" : "BACKTEST →"}
        </button>
      </div>

      {r && <BacktestResult r={r} />}
    </div>
  );
}

function FieldIn({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value)} className="bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-1.5 text-sm text-white font-mono-data focus:outline-none focus:border-[color:var(--border-focus)]" />
    </label>
  );
}

function BacktestResult({ r }) {
  const m = r.metrics;
  return (
    <div className="space-y-4" data-testid="bt-result">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]" data-testid="bt-metrics">
        <Cell label="TOTAL RETURN" value={`${m.total_return_pct}%`} tone={m.total_return_pct >= 0 ? "bull" : "bear"} />
        <Cell label="CAGR" value={`${m.cagr_pct}%`} tone={m.cagr_pct >= 0 ? "bull" : "bear"} />
        <Cell label="SHARPE" value={m.sharpe} tone="gold" />
        <Cell label="MAX DRAWDOWN" value={`${m.max_drawdown_pct}%`} tone="bear" />
        <Cell label="FINAL EQUITY" value={`$${fmt(r.final_equity)}`} tone="gold" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]">
        <Cell label="SORTINO" value={m.sortino} />
        <Cell label="CALMAR" value={m.calmar} />
        <Cell label="WIN RATE" value={`${m.win_rate_pct}%`} tone={m.win_rate_pct > 50 ? "bull" : "bear"} />
        <Cell label="PROFIT FACTOR" value={m.profit_factor} tone={m.profit_factor > 1 ? "bull" : "bear"} />
        <Cell label="TRADES" value={m.trades} />
      </div>
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4" data-testid="bt-equity-chart">
        <div className="text-[10px] font-mono-data text-gold tracking-widest mb-2">◆ EQUITY CURVE · {r.symbol} · {r.strategy}</div>
        <div style={{ height: 320, minWidth: 300, minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <LineChart data={r.equity_curve} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="i" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#27272A" }} />
              <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A", fontSize: 11, fontFamily: "JetBrains Mono" }} labelStyle={{ color: "#A1A1AA" }} itemStyle={{ color: "#fff" }} />
              <ReferenceLine y={r.equity_curve[0]?.equity} stroke="#52525B" strokeDasharray="3 3" />
              <Line type="monotone" dataKey="equity" stroke="#FFD700" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, tone }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : tone === "gold" ? "text-gold" : "text-white";
  return (
    <div className="p-3 border-r border-b border-[color:var(--border-default)]">
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-lg mt-1 ${c}`}>{value}</div>
    </div>
  );
}
